import { useEffect, useMemo } from "react";

import LoadingSpinner from "@/components/LoadingSpinner";
import { useAuth } from "@/context/auth-context";
import { cn } from "@/lib/utils";
import { usePromptRegistry, usePromptVersion, usePromptVersions } from "@/queries";
import type { Message, Template } from "@/types";
import { getStringContent } from "@/utils/utils";

interface PromptEntityPreviewProps {
  workspaceId: string;
  entityId: string;
  versionNumber?: string;
  onReadyChange: (isReady: boolean) => void;
}

const MAX_VISIBLE_MESSAGES = 4;
const MAX_CONTENT_LENGTH = 420;

const truncate = (value: string, maxLength = MAX_CONTENT_LENGTH) => {
  const normalized = value.trim();
  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, maxLength).trimEnd()}…`;
};

const getMessagePreviewText = (message: Message) => {
  if (message.role === "assistant" && "function_call" in message && message.function_call) {
    return `Function call: ${message.function_call.name}\n${message.function_call.arguments}`;
  }

  return getStringContent(message);
};

const renderVariableChips = (variables: string[] | undefined) => {
  if (!variables?.length) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {variables.slice(0, 8).map(variable => (
        <span
          key={variable}
          className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700"
        >
          {variable}
        </span>
      ))}
      {variables.length > 8 && (
        <span className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-500">
          +{variables.length - 8} more
        </span>
      )}
    </div>
  );
};

const PromptTemplateContent = ({ template }: { template: Template }) => {
  if (template.type === "completion") {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-5">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Completion template
        </p>
        <p className="whitespace-pre-wrap text-[15px] leading-7 text-slate-800">
          {truncate(getStringContent(template)) || "Empty completion prompt"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {template.messages.slice(0, MAX_VISIBLE_MESSAGES).map(message => {
        const content = truncate(getMessagePreviewText(message));

        return (
          <div
            key={`${message.role}-${content.slice(0, 80)}`}
            className="rounded-xl border border-slate-200 bg-slate-50/80 p-4"
          >
            <div className="mb-2 flex items-center justify-between gap-3">
              <span
                className={cn(
                  "rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide",
                  message.role === "system" && "bg-violet-100 text-violet-700",
                  message.role === "user" && "bg-blue-100 text-blue-700",
                  message.role === "assistant" && "bg-emerald-100 text-emerald-700",
                  message.role !== "system" &&
                    message.role !== "user" &&
                    message.role !== "assistant" &&
                    "bg-slate-200 text-slate-700",
                )}
              >
                {message.role}
              </span>
              {message.template_format && (
                <span className="text-xs text-slate-400">{message.template_format}</span>
              )}
            </div>
            <p className="whitespace-pre-wrap text-[15px] leading-7 text-slate-800">
              {content || "Empty message"}
            </p>
          </div>
        );
      })}
      {template.messages.length > MAX_VISIBLE_MESSAGES && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-4 text-sm font-medium text-slate-500">
          +{template.messages.length - MAX_VISIBLE_MESSAGES} more messages
        </div>
      )}
    </div>
  );
};

export const PromptEntityPreview = ({
  workspaceId,
  entityId,
  versionNumber,
  onReadyChange,
}: PromptEntityPreviewProps) => {
  const userToken = useAuth()?.userToken ?? "";
  const promptId = Number(entityId);
  const requestedVersionNumber = versionNumber ? Number(versionNumber) : undefined;

  const promptRegistryQuery = usePromptRegistry(userToken, promptId, false);
  const promptVersionsQuery = usePromptVersions(userToken, {
    workspaceId: Number(workspaceId),
    promptRegistryId: promptId,
    page: 1,
    perPage: 1,
  });

  const latestVersionNumber = promptVersionsQuery.data?.pages?.[0]?.prompt_versions?.[0]?.number;
  const resolvedVersionNumber = requestedVersionNumber ?? latestVersionNumber;
  const promptVersionQuery = usePromptVersion(
    userToken,
    promptId,
    resolvedVersionNumber ?? null,
    Boolean(resolvedVersionNumber),
  );

  const registry = promptRegistryQuery.data;
  const version = promptVersionQuery.data;
  const template = useMemo(
    () => version?.rendered_template ?? version?.prompt_template,
    [version?.prompt_template, version?.rendered_template],
  );

  const isLoading =
    promptRegistryQuery.isLoading ||
    promptVersionsQuery.isLoading ||
    (Boolean(resolvedVersionNumber) && promptVersionQuery.isLoading);
  const hasError =
    promptRegistryQuery.isError || promptVersionsQuery.isError || promptVersionQuery.isError;
  const isReady = !isLoading && (hasError || Boolean(registry && version && template));

  useEffect(() => {
    onReadyChange(isReady);
  }, [isReady, onReadyChange]);

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-sm">
        <LoadingSpinner />
      </div>
    );
  }

  if (hasError || !registry || !version || !template) {
    return (
      <div className="h-full w-full rounded-2xl border border-slate-200 bg-white p-10 shadow-sm">
        <span className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-medium tracking-wide text-rose-700">
          PROMPT PREVIEW UNAVAILABLE
        </span>
        <h1 className="mt-6 text-3xl font-semibold tracking-tight text-slate-900">
          Could not load this prompt.
        </h1>
        <p className="mt-4 max-w-xl text-sm leading-6 text-slate-600">
          The preview renderer could not fetch the prompt registry or version data for prompt{" "}
          {entityId}.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 bg-white px-10 py-8">
        <div className="mb-5 flex items-center justify-between gap-4">
          <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium tracking-wide text-amber-700">
            {registry.is_snippet ? "SNIPPET" : "PROMPT"}
          </span>
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700">
            {requestedVersionNumber ? `v${version.number}` : `Latest · v${version.number}`}
          </span>
        </div>
        <h1 className="line-clamp-2 text-4xl font-semibold tracking-tight text-slate-950">
          {registry.prompt_name}
        </h1>
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <span className="rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
            {template.type}
          </span>
          {version.metadata?.model?.provider && (
            <span className="rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
              {version.metadata.model.provider}
            </span>
          )}
          {version.metadata?.model?.name && (
            <span className="rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
              {version.metadata.model.name}
            </span>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden bg-white px-10 py-8">
        <div className="grid h-full grid-cols-[1fr_280px] gap-6">
          <div className="min-h-0 overflow-hidden">
            <PromptTemplateContent template={template} />
          </div>
          <aside className="space-y-5 rounded-xl border border-slate-200 bg-slate-50/70 p-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Variables
              </p>
              <div className="mt-3">
                {renderVariableChips(template.input_variables) ?? (
                  <p className="text-sm text-slate-500">No input variables</p>
                )}
              </div>
            </div>
            {version.commit_message && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Version note
                </p>
                <p className="mt-2 line-clamp-4 text-sm leading-6 text-slate-700">
                  {version.commit_message}
                </p>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};
